import { useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import {
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';
import { Box, Flex, Text } from '@chakra-ui/react';

import { toMoney } from 'src/legacy/Utils/Conversor';

import { useToast } from 'src/hooks';
import { useMeQuery } from 'src/hooks/queries/user';
import { PaymentType } from 'src/types/dto';

import { useEventData } from '../../hooks';
import { useEventContext } from '../../provider';
import { useCartStore, useCheckoutStore, useDiscountStore, usePrivateLinkStore } from '../../stores';
import { getCartItems } from '../../utils';

import { CartItems } from './CartItems';
import { CouponApplication } from './CouponApplication';
import { DiscountWarning } from './DiscountWarning';

const CheckoutTickets = () => {
  const hasCalledOpenOrEditSell = useRef(false);
  const navigate = useNavigate();
  const { eventID } = useParams();
  const location = useLocation();
  const { openOrEditSell } = useEventContext();
  const event = useEventData();
  const { getEventCart } = useCartStore();
  const { getEventCheckout } = useCheckoutStore();
  const discountStore = useDiscountStore();
  const meQuery = useMeQuery();
  const toast = useToast();

  const checkout = getEventCheckout(eventID!);

  const { fetchEventInfo } = usePrivateLinkStore();
  const urlParams = new URLSearchParams(window.location.search);
  const privateLinkName = urlParams.get('linkname');  
  const coupon = urlParams.get('coupon');
  const newCookieValue = JSON.stringify({ privateLinkName, eventID });

  const checkoutBag = getCartItems({
    cart: getEventCart(eventID!),
    coupon: discountStore.discount,
  })?.reduce(
    (acc, item) => ({
      totalFee:
        acc.totalFee +
        (event?.paymentTypes?.includes(PaymentType.CreditCard)
          ? item.creditCardFee
          : 0),
      totalTickets: acc.totalTickets + item.total,
      totalDiscount: acc.totalDiscount + item.discount,
      isCouponApplied: item.isCouponApplied || acc.isCouponApplied,
    }),
    {
      totalFee: 0,
      totalTickets: 0,
      totalDiscount: 0,
      isCouponApplied: false,
    },
  );

  const { mutate } = discountStore.useApplyMutation();

  useEffect(() => {
    if (!discountStore.discount && coupon) {
      mutate({
        sellId: checkout.sellId,
        eventId: eventID!,
        couponName: coupon,
      });
    }
  }, [checkout.sellId, coupon, discountStore.discount, eventID, mutate]);

  useEffect(() => {
    if (event?.integrations?.pixelId &&window.location.href.includes(event.id) ) {
      ReactPixel.trackSingle(event?.integrations?.pixelId,'AddToCart', {
        content_type: 'product',
        content_name: event?.name,
        content_ids: [event?.id],
      });
    }
  }, [event?.id, event?.name, event?.integrations?.pixelId]);

  const { clearDiscount } = discountStore;
  useEffect(() => {
    (async () => {
      if (!checkout.sellId && (!meQuery.isLoading && meQuery.data?.email) && !hasCalledOpenOrEditSell.current) {
        try {
          hasCalledOpenOrEditSell.current = true;
          await openOrEditSell();
        } catch (er) {
          clearDiscount();
          navigate(location.pathname);
          toast({
            description:
              (er as AxiosError).message ?? 'Ocorreu um erro inesperado',
            status: 'warning',
          });
        }
      }
    })();
  }, [clearDiscount, navigate, location.pathname, meQuery.data?.email, openOrEditSell, checkout.sellId, toast, privateLinkName, newCookieValue, meQuery.isLoading]);

  useEffect(() => {
    if (eventID && privateLinkName) {
      fetchEventInfo(eventID, privateLinkName);
    }
  }, [eventID, privateLinkName, fetchEventInfo]);

  const isThereFreeTicket = getEventCart(eventID!).some(
    ticket => ticket.price === 0 && ticket.quantity > 0,
  );

  return (
    <Box
      w="full"
      mx="auto"
      pb="8"
      px={{ base: 0, md: 5 }}
      ml={{ base: 0, md: 1 }}>
      {!!discountStore.discount && (
        <DiscountWarning
          data={discountStore.discount}
          isCouponApplied={checkoutBag.isCouponApplied}
        />
      )}
      <CartItems />
      {!discountStore.discount && <CouponApplication mt="4" />}
      <Flex
        align="center"
        justify="space-between"
        color="gray.400"
        pt={6}
        pb={3}>
        <Text>Ingressos</Text>
        <Text>{toMoney(checkoutBag.totalTickets)}</Text>
      </Flex>
      {event?.paymentTypes?.includes(PaymentType.CreditCard) && (
        <Flex align="center" justify="space-between" color="gray.400" pb={3}>
          <Text>Taxas</Text>
          <Text>{toMoney(checkoutBag.totalFee)}</Text>
        </Flex>
      )}
      {checkoutBag.isCouponApplied && (
        <Flex align="center" justify="space-between" color="gray.400" pb={3}>
          <Text>{`Cupom "${discountStore.discount?.name ?? ''}"`}</Text>
          <Text color="green.500">{`- ${toMoney(
            checkoutBag.totalDiscount,
          )}`}</Text>
        </Flex>
      )}
      <Flex align="center" justify="space-between" fontWeight="bold" pb={3}>
        <Text>Total</Text>
        <Text>
          {checkoutBag.totalTickets > 0 || !isThereFreeTicket ? (
            <>
              {toMoney(
                checkoutBag.isCouponApplied && !!discountStore.discount
                  ? checkoutBag.totalTickets -
                  checkoutBag.totalDiscount +
                  (event?.paymentTypes?.includes(PaymentType.CreditCard)
                    ? checkoutBag.totalFee
                    : 0)
                  : checkoutBag.totalTickets + checkoutBag.totalFee,
              )}
            </>
          ) : (
            <Text>Gratuito</Text>
          )}
        </Text>
      </Flex>
    </Box>
  );
};

export default CheckoutTickets;
